import { useEffect, useRef } from 'react';

/**
 * Fires `onReached` once when the page scroll depth passes `thresholdPct` (0–100).
 * Uses a passive scroll listener and cleans up on unmount.
 */
export function useScrollDepth(thresholdPct: number, onReached: () => void): void {
  const firedRef = useRef(false);
  const callbackRef = useRef(onReached);

  useEffect(() => {
    callbackRef.current = onReached;
  }, [onReached]);

  useEffect(() => {
    firedRef.current = false;

    const handleScroll = () => {
      if (firedRef.current) return;

      const scrolled = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;

      const pct = (scrolled / docHeight) * 100;
      if (pct >= thresholdPct) {
        firedRef.current = true;
        callbackRef.current();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [thresholdPct]);
}
